import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/core';
import { useDispatch, useSelector } from 'react-redux';
import StepUploader from './components/StepUploader';
import StepProgramDetector from './components/StepProgramDetector';
import StepProcessing from './components/StepProcessing';
import StepExerciseChecker from './components/StepExerciseChecker';
import StepProgressBar from './components/StepProgressBar';
import {
  fetchImportQuota, resetImport, fetchPendingImports, fetchAiProviderHealth,
} from './ducks';
import { IMPORT_STEPS } from './ducks/actionTypes';

const stepFadeIn = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled('div')`
  position: fixed;
  top: 49px;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background: #f0f0f0;
  z-index: 10;
  padding: 40px 20px;
  overflow-y: auto;

  &::before {
    content: '';
    position: fixed;
    top: 49px;
    left: 0;
    right: 0;
    height: 280px;
    background: linear-gradient(180deg, rgba(45, 45, 68, 0.04) 0%, transparent 100%);
    pointer-events: none;
    z-index: 0;
  }
`;

const WizardStack = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 0;
  position: relative;
  z-index: 1;
  padding-top: 20px;
`;

const StepAnimWrapper = styled('div')`
  animation: ${stepFadeIn} 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  width: 100%;
  display: flex;
  justify-content: center;
`;

const STEPS = {
  1: StepUploader,
  2: StepProgramDetector,
  3: StepProcessing,
  4: StepExerciseChecker,
};

const AIProgramImportLayout = () => {
  const dispatch = useDispatch();
  const currentStep = useSelector((state) => state.aiProgramImport.currentStep);
  // Auth slice hydrates async — accountCode is undefined for the first few
  // ticks after mount. Subscribe to it so the quota fetch runs as soon as it
  // resolves (and not before, which would hit /accounts/undefined/...).
  const accountCode = useSelector(
    (state) => state.auth?.data?.currentUser?.accountCode,
  );
  const matchCount = useSelector((state) => state.aiProgramImport.matches.length);
  const createdCount = useSelector(
    (state) => state.aiProgramImport.createdPrograms.length,
  );
  const StepComponent = STEPS[currentStep] || StepUploader;

  // Reset stale wizard state on every fresh entry to /import/program. Two
  // cases this covers:
  //   1. Coach returns after a successful import (createdPrograms populated).
  //   2. Coach started resuming a draft (currentStep advanced to REVIEW via
  //      LOAD_IMPORT_FOR_REVIEW_FULFILLED), navigated away without finishing,
  //      and re-entered the wizard. Without this reset they'd land back on
  //      Step 4 with the resumed draft's stale data instead of the Upload
  //      step. RESET_IMPORT preserves quota + pendingImports + the LD
  //      home-landing signal, so the only loss is intentional: the
  //      in-progress wizard flow.
  // didResetRef ensures the reset only fires once per mount — without it,
  // the effect would loop because RESET_IMPORT clears the signal that
  // triggered it.
  const didResetRef = useRef(false);
  useEffect(() => {
    if (didResetRef.current) return;
    didResetRef.current = true;
    // Don't wipe a draft that was just hydrated for review. Resuming from the
    // Program Navigator awaits loadImportForReview (which sets REVIEW +
    // matches) and THEN navigates here, so the layout mounts fresh on top of
    // already-loaded review state — an unconditional reset would clear the
    // matches and bounce the coach back to Upload. createdPrograms === 0
    // distinguishes an in-progress review from a return visit after a
    // completed import, which still resets to Upload as intended.
    const isHydratedReview = currentStep === IMPORT_STEPS.REVIEW
      && matchCount > 0
      && createdCount === 0;
    if (isHydratedReview) return;
    dispatch(resetImport());
  }, [dispatch]);

  // Hydrate quota state once accountCode is known so the uploader pill renders
  // the real tier / cap / remaining instead of an empty slot. The reducer
  // keeps quota around across step changes; we don't refetch when navigating
  // between steps, only on mount and after extract (where deduction happens).
  // Pending imports come along for the ride so Step 1's "Resume an
  // unfinished import" surface has data immediately — this is the wizard's
  // only entry point to pending imports until the new Home Navigator
  // ships, so the fetch belongs here (not just in the Navigator).
  useEffect(() => {
    if (!accountCode) return;
    dispatch(fetchImportQuota());
    dispatch(fetchPendingImports());
    // Provider liveness so the upload step can warn + block before the coach
    // spends an import. The result is cached 60s server-side, so this is cheap
    // and we re-check on the Import click to catch a provider dropping after.
    dispatch(fetchAiProviderHealth());
  }, [dispatch, accountCode]);

  return (
    <PageWrapper>
      <WizardStack>
        <StepProgressBar />
        <StepAnimWrapper key={currentStep}>
          <StepComponent />
        </StepAnimWrapper>
      </WizardStack>
    </PageWrapper>
  );
};

export default AIProgramImportLayout;
